import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { CommonModule as AppCommonModule } from '@modules/common/common.module';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { type SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { ElementsModule } from '@modules/elements/elements.module';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { UtilsModule } from '@modules/utils/utils.module';
import { map, Subject } from 'rxjs';
import { BoardsFolderContextMenuDirective } from '../../menus/boards-folder-context-menu.directive';
import { BoardsFoldersListContextMenuDirective } from '../../menus/folders-list-context-menu.directive';
import { BoardsSidebarContextMenuDirective } from '../../menus/boards-sidebar-context-menu.directive';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { type BoardSidebarView } from '../../../types/board';
import { type BoardFolder } from '../../../types/board';
import { boardsFromDragData } from '../../shared/board-drag-data.util';
import { BoardsQuickFormComponent } from '../../creation/boards-quick-form/boards-quick-form.component';

@Component({
  selector: 'app-boards-sidebar',
  templateUrl: './boards-sidebar.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [heightAnimation],
  imports: [
    SidebarItemComponent,
    ElementsModule,
    UtilsModule,
    AppCommonModule,
    BoardsQuickFormComponent,
    BoardsFolderContextMenuDirective,
    BoardsFoldersListContextMenuDirective,
    BoardsSidebarContextMenuDirective,
    ContextMenuStateIndicatorComponent,
    PopoverDirective,
    NgTemplateOutlet,
    IconComponent,
    DragNDropModule,
  ],
})
export class BoardsSidebarComponent {
  public readonly openFolder = output<string>();
  public readonly selectFolderContents = output<string>();
  protected readonly store = inject(BoardsStore);
  protected readonly listStore = inject(BoardListStore);
  protected readonly visibleFolders = computed(() => this.store.sortedFolders().filter(folder =>
    this.listStore.view() === 'deleted' ? folder.deleted : this.listStore.view() === 'archived' ? folder.archived && !folder.deleted : !folder.archived && !folder.deleted));
  protected readonly SidebarSplitViewKey = SidebarSplitViewKey;

  /** Icon-only rail state of this sidebar's own split-view pane — same key `app-sidebar-knots`/`app-sidebar-tags` already read. */
  protected readonly minimized = toSignal(inject(SplitViewService).getMinimized(SidebarSplitViewKey.boards), {
    initialValue: false,
  });

  /** The count beside "All boards": the live boards (not archived, not in Trash). */
  protected readonly allBoardsCount = computed(() =>
    // Exact only once a whole read has finished (R13-SCALE-3: the first page arrives before the rest).
    this.store.boardsComplete() ? this.store.boards().filter((board) => !board.archived && !board.deleted).length : undefined,
  );

  protected readonly baseItems: Array<SidebarItem & { id: BoardSidebarView }> = [
    { id: 'all', title: 'All boards', icon: 'sapphire-board-view' },
    // Recent is back (Johanna, 2026-10-21: it was dropped on 2026-10-20; keep it in case it's needed).
    { id: 'recent', title: 'Recent', icon: 'sapphire-recent' },
    { id: 'flagged', title: 'Flagged', icon: 'sapphire-flagged' },
    { id: 'followup', title: 'Follow Up', icon: 'sapphire-follow-up' },
    { id: 'snoozed', title: 'Snoozed', icon: 'sapphire-snooze' },
    { id: 'templates', title: 'Templates', icon: 'sapphire-template' },
    { id: 'archived', title: 'Archive', icon: 'sapphire-archive' },
    { id: 'deleted', title: 'Trash', icon: 'sapphire-trash' },
  ];

  /** Same persisted-expand intent as `app-sidebar-tags`' own `expanded` — survives leaving and returning to
   * Boards, instead of always starting expanded (SBUX-988 review #6). Constructed manually (not via the
   * `@LSBoolean` class decorator) so it can back a real signal instead of a plain field `OnPush` would
   * never notice change on. */
  private readonly foldersExpandedStorage = new LocalStorageItem<boolean>('boards-sidebar.folders-expanded', {
    default: true,
    parse: ({ raw }) => (raw === false || raw === 'false' ? false : raw === true || raw === 'true' ? true : true),
  });
  protected readonly foldersExpanded = toSignal(this.foldersExpandedStorage.get().pipe(map((value) => value ?? true)), {
    initialValue: this.foldersExpandedStorage.getSync() ?? true,
  });

  protected readonly quickFormPopoverClose = new Subject<void>();

  protected selectView(id: BoardSidebarView): void {
    if (this.store.editorBoardId()) this.store.closeEditor();
    this.listStore.setView(id);
  }

  protected selectFolder(folderId: string, selectAll = false): void {
    if (selectAll) this.selectFolderContents.emit(folderId);
    else this.openFolder.emit(folderId);
  }

  /** The "Folders" label opens every folder in the list, as Sheets' and Files' "Folders" do, and leaves the
   * section folded or open: only its arrow folds it (Johanna, 2026-10-26: the label used to fold it). */
  protected selectFolders(): void {
    if (this.store.editorBoardId()) this.store.closeEditor();
    this.listStore.showFolders();
  }

  protected setFoldersExpanded(expanded: boolean): void {
    this.foldersExpandedStorage.set(expanded);
  }

  protected createFolder(title: string): void {
    this.store.createFolder(title).subscribe((id) => {
      if (id) this.showCreatedFolder(id);
    });
  }

  /** A folder made with the "+" or a row menu's "Add new folder" opens in the list, with Folders unfolded. */
  protected showCreatedFolder(id: string): void {
    if (this.store.editorBoardId()) this.store.closeEditor();
    this.setFoldersExpanded(true);
    this.listStore.setFolder(id);
  }

  protected openCreatedBoard(folderId: string, boardId: string): void {
    if (this.store.editorBoardId()) this.store.closeEditor();
    this.listStore.setFolder(folderId);
    this.store.selectBoard(boardId);
  }

  // --- Drag and drop ---
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /** Base sidebar rows accept a board drag only — `dndDropOnView` has no folder-triage meaning to apply
   * (folders don't have a Templates/Snooze/Follow Up concept), so a folder drag is rejected here rather than
   * highlighted as a valid target that then does nothing on drop. */
  protected readonly dndPredicate = (dragData: DragData): boolean => boardsFromDragData(dragData).length > 0;

  /** Folders are flat (see devdoc/specs/boards.md), so a folder row accepts a board drag only — filing it
   * there — never another folder. It does not depend on the folder, so every row binds this one function
   * rather than a new closure per row on every change detection pass. */
  protected readonly folderDndPredicate = (dragData: DragData): boolean => boardsFromDragData(dragData).length > 0;

  protected dndDropOnFolder(dragData: DragData, folder: BoardFolder): void {
    const boardIds = boardsFromDragData(dragData).map((board) => board.id);
    if (boardIds.length) {
      this.store.moveBoardsToFolder(boardIds, folder.id);
    }
  }

  /** A board dropped on a base sidebar row applies that row's own triage meaning — the same "Move" a real
   * backend's virtual-folder drop would perform (devdoc/specs/drag-and-drop.md §4.3) — never a redundant
   * toggle on a board that already has that state. Snooze/Follow Up have no room for an interactive date
   * picker mid-drag, so they fall back to the same "tomorrow, 9am" default the postpone menu offers. */
  protected dndDropOnView(dragData: DragData, view: string): void {
    const boards = boardsFromDragData(dragData);
    if (!boards.length) {
      return;
    }
    // Every branch below is a "move to this row" drop, same as a folder drop — restore out of Archive/Trash
    // first (a no-op for a board that's already live), or a board dragged out of Trash onto e.g. Flagged would
    // end up flagged but still deleted, and stay invisible in the very row it was dropped on (SBUX-988
    // review #11). `archiveBoard`/`deleteBoard` below already clear the OTHER of the two themselves.
    //
    // The 'archived'/'deleted' cases are handled entirely by `archiveBoard`/`deleteBoard` below, which
    // already both clear the OTHER flag AND (via a plain toggle) flip THIS one on in a single PUT — so
    // they must NOT go through this blanket restore first. Doing so used to fire a second, redundant PUT
    // for the very same board (`restoreBoards`' own `{archived: false, ...}`, immediately followed by
    // `archiveBoard`'s `{archived: true, ...}`), racing it: whichever response the SERVER happened to
    // apply last decided the board's real archived state, independent of which request was actually the
    // user's real intent (SBUX-988 review — QA: "sidebar drag-and-drop triggers concurrent Restore/
    // Archive requests"). Filtering to boards not already in the target state below makes a re-drag onto
    // the board's own current row (e.g. an already-archived board dropped on Archive again) a true no-op —
    // no request at all — instead of a restore-then-re-archive round trip.
    // Boards/Recent are left out too: `moveToFolder` below already clears archived/deleted in its own
    // write, so restoring first would send two PUTs for the same fields.
    if (view !== 'archived' && view !== 'deleted' && view !== 'all' && view !== 'recent') {
      this.store.restoreBoards(boards.map((board) => board.id));
    }
    switch (view) {
      case 'flagged':
        boards.filter((board) => !board.flagged).forEach((board) => this.store.toggleFlag(board.id));
        break;
      case 'archived':
        boards.filter((board) => !board.archived).forEach((board) => this.store.archiveBoard(board.id));
        break;
      case 'deleted':
        boards.filter((board) => !board.deleted).forEach((board) => this.store.deleteBoard(board.id));
        break;
      case 'templates':
        this.store.setBoardsTemplate(boards.filter((board) => !board.isTemplate).map((board) => board.id), true);
        break;
      case 'followup': {
        const at = relativeDateToWorkDayStart('tomorrow', this.displayTimeZoneService.timeZone());
        boards.filter((board) => !board.followUp).forEach((board) => this.store.setFollowUp(board.id, at));
        break;
      }
      case 'snoozed': {
        const until = relativeDateToWorkDayStart('tomorrow', this.displayTimeZoneService.timeZone());
        boards.filter((board) => !board.snoozed).forEach((board) => this.store.setSnooze(board.id, until));
        break;
      }
      case 'all':
      case 'recent':
        // Unconditional, not just boards with a folder: `moveToFolder` is also how a folderless Trash/
        // Archive board gets restored, and skipping folderless boards here silently dropped that case
        // entirely (SBUX-988 review #11).
        boards.forEach((board) => this.store.moveToFolder(board.id, null));
        break;
      default:
        break;
    }
  }
}
